import {
  COMPONENT_COLOR_THEMES,
  COMPONENT_VALUE_TYPES as types,
  EVENT_BUTTON_STYLE_TYPE,
  REGIONS,
} from 'shared/constants';
import { PropsSchemaList } from 'shared/types/PropsSchema';

export interface Props {
  region: 'US' | 'CN';
  background: {
    desktopImage: string;
    mobileImage: string;
  },
  topContent: {
    showHomeHeader: boolean;
    homeHeaderTarget: string;
    homeHeaderText: string;
    homeHeaderOpenInNewTab: boolean;
    homeHeaderIcon: string;
    headerImage: string;
    title: string;
    mobileTitle: string;
    subtitle: string;
    mobileSubtitle: string;
    showShareIcon: boolean;
    buttonStyle: string;
    buttonTheme: string;
    buttons: [{
      text: string;
      target: string;
      openInNewTab: boolean;
      addToCalendar: boolean;
    }];
  },
  bottomContent: {
    buttonStyle: string;
    buttonTheme: string;
    buttons: [{
      text: string;
      target: string;
      openInNewTab: boolean;
    }];
    showScrollIndicator: boolean;
  };
  eventInformation?: {
    title: string;
    description: string;
    location: string;
    startDateTime: {
      date: string;
      timezone: string;
    };
    endDateTime: {
      date: string;
      timezone: string;
    };
  };
  socialLinks?: {
    metadata: {
      url: string;
      title: string;
      description: string;
    };
    icons: {
      icon: string;
    }[];
  };
}

const propsSchema: PropsSchemaList = [
  {
    key: 'region',
    label: 'Region',
    options: REGIONS,
    type: types.ENUM,
  },
  {
    key: 'background',
    label: 'Background Images',
    type: types.OBJECT,
    children: [
      {
        key: 'desktopImage',
        label: 'Desktop Background Image',
        type: types.S3_ASSET_URL,
      },
      {
        key: 'mobileImage',
        label: 'Mobile Background Image',
        type: types.S3_ASSET_URL,
      },
    ],
  },
  {
    key: 'topContent',
    label: 'Top Content',
    type: types.OBJECT,
    children: [
      {
        key: 'showHomeHeader',
        label: 'Show Home Header',
        type: types.BOOLEAN,
      },
      {
        key: 'homeHeaderText',
        label: 'Home Header Text',
        type: types.TEXT,
      },
      {
        key: 'homeHeaderTarget',
        label: 'Home Header Text',
        type: types.TEXT,
      },
      {
        key: 'homeHeaderOpenInNewTab',
        label: 'Home Header Open in New Tab',
        type: types.BOOLEAN,
      },
      {
        key: 'homeHeaderIcon',
        label: 'Home Header Icon',
        type: types.TEXT,
        // type: types.ENUM,
        // options: ['DEFAULT', 'FULL', 'SIMPLE', 'FX SIMPLE']
      },
      {
        key: 'headerImage',
        label: 'Header Image',
        type: types.S3_ASSET_URL,
      },
      {
        key: 'title',
        label: 'Title',
        type: types.TINYMCE,
      },
      {
        key: 'mobileTitle',
        label: 'Mobile Title',
        type: types.TINYMCE,
      },
      {
        key: 'subtitle',
        label: 'Subtitle',
        type: types.TINYMCE,
      },
      {
        key: 'mobileSubtitle',
        label: 'Mobile Subtitle',
        type: types.TINYMCE,
      },
      {
        key: 'showShareIcon',
        label: 'Show Share Icon',
        tooltip: 'Uses the data from Social Links to configure each social media icon',
        type: types.BOOLEAN,
      },
      {
        key: 'buttonStyle',
        label: 'Button Style',
        type: types.ENUM,
        options: EVENT_BUTTON_STYLE_TYPE,
      },
      {
        key: 'buttonTheme',
        label: 'Button Theme',
        type: types.ENUM,
        options: COMPONENT_COLOR_THEMES,
      },
      {
        key: 'buttons',
        label: 'Buttons',
        type: types.ARRAY,
        children: [
          {
            key: 'text',
            label: 'Text',
            type: types.TEXT,
          },
          {
            key: 'target',
            label: 'Target URL',
            type: types.TEXT,
          },
          {
            key: 'openInNewTab',
            label: 'Open In New Tab',
            type: types.BOOLEAN,
          },
          {
            key: 'addToCalendar',
            label: 'Add to Calendar (Ignores URL)',
            tooltip: `Add the event to the user's calendar, based on the Event Information settings.`,
            type: types.BOOLEAN,
          },
        ],
      },
    ],
  },
  {
    key: 'bottomContent',
    label: 'Bottom Content',
    type: types.OBJECT,
    children: [
      {
        key: 'buttonStyle',
        label: 'Button Style',
        type: types.ENUM,
        options: EVENT_BUTTON_STYLE_TYPE,
      },
      {
        key: 'buttonTheme',
        label: 'Button Theme',
        type: types.ENUM,
        options: COMPONENT_COLOR_THEMES,
      },
      {
        key: 'buttons',
        label: 'Buttons',
        type: types.ARRAY,
        children: [
          {
            key: 'text',
            label: 'Text',
            type: types.TEXT,
          },
          {
            key: 'target',
            label: 'Target URL',
            type: types.TEXT,
          },
          {
            key: 'openInNewTab',
            label: 'Open In New Tab',
            type: types.BOOLEAN,
          },
        ],
      },
      {
        key: 'showScrollIndicator',
        label: 'Show Scroll Indicator on Desktop',
        type: types.BOOLEAN,
      },
    ],
  },
  {
    key: 'eventInformation',
    label: 'Event Information',
    type: types.OBJECT,
    children: [
      {
        key: 'title',
        label: 'Title',
        type: types.TEXT,
      },
      {
        key: 'description',
        label: 'Description',
        type: types.TEXT,
      },
      {
        key: 'location',
        label: 'Location',
        type: types.TEXT,
      },
      {
        key: 'startDateTime',
        label: 'Start DateTime',
        type: types.DATETIME,
      },
      {
        key: 'endDateTime',
        label: 'End DateTime',
        type: types.DATETIME,
      },
    ]
  },
  {
    key: 'socialLinks',
    label: 'Social Links',
    type: types.OBJECT,
    children: [

      {
        key: 'metadata',
        label: 'Link metadata',
        type: types.OBJECT,
        children: [
          {
            key: 'url',
            label: 'URL',
            type: types.TEXT,
          },
          {
            key: 'title',
            label: 'Title',
            type: types.TEXT,
          },
          {
            key: 'description',
            label: 'Description',
            type: types.TEXT,
          },
        ],
      },
      {
        key: 'icons',
        label: 'Icons',
        type: types.ARRAY,
        children: [
          {
            key: 'icon',
            label: 'Icon',
            type: types.ENUM,
            options: [
              'copylink',
              'email',
              'facebook',
              'linkedin',
              'reddit',
              'twitter',
              'wechat',
              'weibo',
              'whatsapp',
            ]
          },
        ]
      },
    ]
  },
];

const defaultProps: Props = {
  region: 'US',
  background: {
    desktopImage: 'https://genesis-cdn.ff.com/homepage/01_marquee/genesis_cover_image.jpg',
    mobileImage: 'https://genesis-cdn.ff.com/homepage/01_marquee/genesis_cover_image.jpg',
  },
  topContent: {
    showHomeHeader: false,
    homeHeaderText: 'HOME',
    homeHeaderTarget: 'https://www.ff.com',
    homeHeaderOpenInNewTab: false,
    homeHeaderIcon: 'DEFAULT',
    headerImage: 'https://genesis-cdn.ff.com/assets/pages/sop-event/91_2.svg',
    title: '<p style="color: white"><span style="font-size: 38px">FF 91 Futurist Start of Production</span><p>',
    mobileTitle: '<p style="color: white"><span style="font-size: 30px">FF 91 Futurist Start of Production</span><p>',
    subtitle: '<p style="color: white"><span style="font-size: 16px">March.29, 2023 4:30 PM PST. Live Streaming</span><p>',
    mobileSubtitle: '<p style="color: white"><span style="font-size: 16px">March.29, 2023 4:30 PM PST. Live Streaming</span><p>',
    showShareIcon: false,
    buttonStyle: 'UNDERLINED',
    buttonTheme: 'LIGHT',
    buttons: [{
      text: 'FF Intelligent App Livestream',
      target: 'https://www.ff.com',
      openInNewTab: false,
      addToCalendar: false,
    }],
  },
  bottomContent: {
    buttonStyle: 'UNDERLINED',
    buttonTheme: 'LIGHT',
    buttons: [{
      text: 'FF Intelligent App Livestream',
      target: 'https://www.ff.com',
      openInNewTab: false,
    }],
    showScrollIndicator: false,
  },
  eventInformation: {
    title: '',
    description: '',
    location: '',
    startDateTime: {
      date: null,
      timezone: 'America/Los_Angeles',
    },
    endDateTime: {
      date: null,
      timezone: 'America/Los_Angeles',
    },
  },
  socialLinks: {
    metadata: {
      url: '',
      title: '',
      description: '',
    },
    icons: [
      { icon: 'copylink' },
      { icon: 'email' },
      { icon: 'facebook' },
      { icon: 'linkedin' },
      { icon: 'reddit' },
      { icon: 'twitter' },
      { icon: 'wechat' },
      { icon: 'weibo' },
      { icon: 'whatsapp' }
    ]
  }
};

export default {
  title: 'Event Hero Image',
  id: 'EventHeroImage',
  propsSchema,
  defaultProps
};
